'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Search, SlidersHorizontal, Clock, IndianRupee, Star, X, ChevronDown } from 'lucide-react';
import HomeNavbar from '@/app/home-screen/components/HomeNavbar';
import HomeBottomNav from '@/app/home-screen/components/HomeBottomNav';

export interface Ritual {
  id: string;
  name: string;
  sanskrit: string;
  description: string;
  duration: string;
  startingPrice: number;
  category: string;
  occasion: string;
  image: string;
  alt: string;
  popular: boolean;
  rating: number;
  reviewCount: number;
  deity: string;
}

export const RITUALS_DATA: Ritual[] = [
{
  id: 'ganesh-puja',
  name: 'Ganesh Puja',
  sanskrit: 'Ganapati Pooja',
  description: 'Invoke Lord Ganesha to remove obstacles and bless new beginnings.',
  duration: '1–2 hours',
  startingPrice: 1100,
  category: 'Deity Worship',
  occasion: 'New Beginnings',
  image: "https://images.unsplash.com/photo-1695201649878-c8158f2d4da8",
  alt: 'Decorated Ganesh idol with marigold flowers and diyas during puja ceremony',
  popular: true,
  rating: 4.9,
  reviewCount: 1240,
  deity: 'Lord Ganesha'
},
{
  id: 'satyanarayan-puja',
  name: 'Satyanarayan Puja',
  sanskrit: 'Satyanarayana Vrat Katha',
  description: 'A sacred vrat dedicated to Lord Vishnu for prosperity and family blessings.',
  duration: '2–3 hours',
  startingPrice: 1500,
  category: 'Vrat & Katha',
  occasion: 'Prosperity',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_14f58ebf0-1773097647994.png",
  alt: 'Satyanarayan puja setup with banana leaves, fruits, and sacred items arranged on a wooden platform',
  popular: true,
  rating: 4.8,
  reviewCount: 980,
  deity: 'Lord Vishnu'
},
{
  id: 'gruhapravesam',
  name: 'Gruhapravesam',
  sanskrit: 'Vastu Shanti Puja',
  description: 'Purify and bless your new home with Vedic rituals for peace and prosperity.',
  duration: '3–4 hours',
  startingPrice: 3500,
  category: 'Life Milestones',
  occasion: 'New Home',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a43f5fba-1764777902490.png",
  alt: 'Traditional Indian home entrance decorated with rangoli and marigold garlands for housewarming ceremony',
  popular: true,
  rating: 4.9,
  reviewCount: 720,
  deity: 'Vastu Devata'
},
{
  id: 'navagraha-shanti',
  name: 'Navagraha Shanti',
  sanskrit: 'Navgraha Pooja',
  description: 'Appease the nine planetary deities to neutralize malefic effects in your horoscope.',
  duration: '2–3 hours',
  startingPrice: 2100,
  category: 'Planetary Remedies',
  occasion: 'Astrological',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_160f2d815-1764664811341.png",
  alt: 'Nine planetary deity idols arranged in a circle with lit oil lamps and flower offerings',
  popular: false,
  rating: 4.7,
  reviewCount: 430,
  deity: 'Navagrahas'
},
{
  id: 'rudrabhishek',
  name: 'Rudrabhishek',
  sanskrit: 'Rudra Abhisheka',
  description: 'Sacred bathing of Lord Shiva with milk, honey, and holy water while chanting Rudra mantras.',
  duration: '2–3 hours',
  startingPrice: 2500,
  category: 'Deity Worship',
  occasion: 'Spiritual Growth',
  image: "https://images.unsplash.com/photo-1722773129568-b69b6e028811",
  alt: 'Shiva lingam being bathed with milk and adorned with bilva leaves and flowers during Rudrabhishek',
  popular: true,
  rating: 4.9,
  reviewCount: 860,
  deity: 'Lord Shiva'
},
{
  id: 'lakshmi-puja',
  name: 'Lakshmi Puja',
  sanskrit: 'Mahalakshmi Pooja',
  description: 'Worship Goddess Lakshmi for wealth, abundance, and removal of financial obstacles.',
  duration: '1.5–2 hours',
  startingPrice: 1800,
  category: 'Deity Worship',
  occasion: 'Prosperity',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2eff503-1772316544594.png",
  alt: 'Goddess Lakshmi idol decorated with gold ornaments, lotus flowers, and lit diyas for puja',
  popular: true,
  rating: 4.8,
  reviewCount: 1100,
  deity: 'Goddess Lakshmi'
},
{
  id: 'vivah-sanskar',
  name: 'Vivah Sanskar',
  sanskrit: 'Vedic Vivaha',
  description: 'Complete Vedic wedding ceremony with all 16 samskaras performed by experienced priests.',
  duration: '4–6 hours',
  startingPrice: 8500,
  category: 'Life Milestones',
  occasion: 'Wedding',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_161a6ccfe-1784721956749.png",
  alt: 'Hindu wedding ceremony with bride and groom performing saptapadi around sacred fire',
  popular: true,
  rating: 5.0,
  reviewCount: 540,
  deity: 'Agni Devata'
},
{
  id: 'namakarana',
  name: 'Namakarana',
  sanskrit: 'Naming Ceremony',
  description: 'Auspicious naming ceremony for newborns, performed on the 11th or 12th day after birth.',
  duration: '1–1.5 hours',
  startingPrice: 1200,
  category: 'Life Milestones',
  occasion: 'Baby Naming',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17e98c41a-1775379683887.png",
  alt: 'Newborn baby surrounded by flowers and sacred items during traditional Hindu naming ceremony',
  popular: false,
  rating: 4.8,
  reviewCount: 320,
  deity: 'Lord Brahma'
},
{
  id: 'durga-puja',
  name: 'Durga Puja',
  sanskrit: 'Devi Upasana',
  description: 'Invoke the fierce grace of Goddess Durga for protection, courage, and victory over adversity.',
  duration: '2–3 hours',
  startingPrice: 2200,
  category: 'Deity Worship',
  occasion: 'Navratri',
  image: "https://images.unsplash.com/photo-1617875216210-06808d39cf42",
  alt: 'Goddess Durga idol with ten arms holding weapons, decorated with red flowers and golden ornaments',
  popular: false,
  rating: 4.7,
  reviewCount: 610,
  deity: 'Goddess Durga'
},
{
  id: 'havan-homa',
  name: 'Havan / Homa',
  sanskrit: 'Agni Karma',
  description: 'Sacred fire ritual where offerings are made to Agni Devata with specific mantras for purification.',
  duration: '1.5–3 hours',
  startingPrice: 2000,
  category: 'Fire Rituals',
  occasion: 'Purification',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_104726711-1764664810416.png",
  alt: 'Sacred fire havan kund with flames rising as priest performs ritual with ghee and herbs',
  popular: false,
  rating: 4.6,
  reviewCount: 480,
  deity: 'Agni Devata'
},
{
  id: 'shraddha-karma',
  name: 'Shraddha Karma',
  sanskrit: 'Pitru Tarpan',
  description: 'Ancestral rites to honor and liberate departed souls, performed during Pitru Paksha.',
  duration: '2–3 hours',
  startingPrice: 1800,
  category: 'Ancestral Rites',
  occasion: 'Pitru Paksha',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19215cd0e-1785773230987.png",
  alt: 'Priest performing tarpan ritual near a river with sesame seeds and water offerings for ancestors',
  popular: false,
  rating: 4.7,
  reviewCount: 290,
  deity: 'Pitru Devatas'
},
{
  id: 'vidyarambha',
  name: 'Vidyarambha',
  sanskrit: 'Aksharabhyasam',
  description: 'Auspicious initiation of learning for children, invoking Goddess Saraswati for wisdom.',
  duration: '1–1.5 hours',
  startingPrice: 1100,
  category: 'Life Milestones',
  occasion: 'Education',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e978fc06-1783936242918.png",
  alt: 'Child writing first letters on rice plate during Vidyarambha ceremony with priest guidance',
  popular: false,
  rating: 4.8,
  reviewCount: 210,
  deity: 'Goddess Saraswati'
}];


const CATEGORIES = ['All', 'Deity Worship', 'Vrat & Katha', 'Life Milestones', 'Planetary Remedies', 'Fire Rituals', 'Ancestral Rites'];
const OCCASIONS = ['All', 'New Beginnings', 'Prosperity', 'New Home', 'Astrological', 'Spiritual Growth', 'Wedding', 'Baby Naming', 'Navratri', 'Purification', 'Pitru Paksha', 'Education'];
const SORT_OPTIONS = ['Most Popular', 'Highest Rated', 'Price: Low to High', 'Price: High to Low'];

function RitualCard({ ritual }: {ritual: Ritual;}) {
  const router = useRouter();
  return (
    <Link href={`/rituals/${ritual.id}`} className="group block">
      <div className="bg-white rounded-2xl border border-gold/20 overflow-hidden card-hover shadow-sm hover:shadow-xl hover:border-gold/40 transition-all duration-300">
        {/* Image */}
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-saffron/10 to-amber-50">
          <img
            src={ritual.image}
            alt={ritual.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/assets/images/no_image.png';
            }} />
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          {/* Popular badge */}
          {ritual.popular &&
          <div className="absolute top-3 left-3 flex items-center gap-1 bg-saffron text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md">
              <Star size={9} fill="white" />
              Popular
            </div>
          }
          {/* Duration pill */}
          <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-1 rounded-full">
            <Clock size={9} />
            {ritual.duration}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="mb-1">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-saffron">{ritual.category}</span>
          </div>
          <h3 className="font-display text-base font-bold text-maroon leading-tight mb-0.5">{ritual.name}</h3>
          <p className="text-[11px] text-maroon/50 italic font-body mb-2">{ritual.sanskrit}</p>
          <p className="text-xs text-maroon/70 font-body leading-relaxed line-clamp-2 mb-3">{ritual.description}</p>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-3">
            <Star size={11} className="text-gold fill-gold" />
            <span className="text-xs font-bold text-maroon">{ritual.rating}</span>
            <span className="text-[10px] text-maroon/40">({ritual.reviewCount})</span>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-gold/15">
            <div>
              <span className="text-[10px] text-maroon/50 font-body">Starting from</span>
              <div className="flex items-center gap-0.5">
                <IndianRupee size={12} className="text-maroon font-bold" />
                <span className="text-sm font-bold text-maroon">{ritual.startingPrice.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); router.push('/booking-flow'); }}
              className="bg-maroon text-ivory text-xs font-semibold px-4 py-2 rounded-full btn-glow hover:bg-saffron transition-colors duration-200"
            >
              Book
            </button>
          </div>
        </div>
      </div>
    </Link>);

}

export default function RitualsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedOccasion, setSelectedOccasion] = useState('All');
  const [sortBy, setSortBy] = useState('Most Popular');
  const [showPopularOnly, setShowPopularOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = [...RITUALS_DATA];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
        r.name.toLowerCase().includes(q) ||
        r.sanskrit.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.deity.toLowerCase().includes(q)
      );
    }

    if (selectedCategory !== 'All') {
      list = list.filter((r) => r.category === selectedCategory);
    }

    if (selectedOccasion !== 'All') {
      list = list.filter((r) => r.occasion === selectedOccasion);
    }

    if (showPopularOnly) {
      list = list.filter((r) => r.popular);
    }

    switch (sortBy) {
      case 'Highest Rated':
        list.sort((a, b) => b.rating - a.rating);
        break;
      case 'Price: Low to High':
        list.sort((a, b) => a.startingPrice - b.startingPrice);
        break;
      case 'Price: High to Low':
        list.sort((a, b) => b.startingPrice - a.startingPrice);
        break;
      default:
        list.sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0) || b.reviewCount - a.reviewCount);
    }

    return list;
  }, [searchQuery, selectedCategory, selectedOccasion, sortBy, showPopularOnly]);

  const activeFilterCount = [
  selectedCategory !== 'All',
  selectedOccasion !== 'All',
  showPopularOnly].
  filter(Boolean).length;

  const clearFilters = () => {
    setSelectedCategory('All');
    setSelectedOccasion('All');
    setShowPopularOnly(false);
    setSortBy('Most Popular');
  };

  return (
    <div className="min-h-screen bg-ivory overflow-x-hidden">
      <HomeNavbar />

      {/* Hero Banner */}
      <div className="relative pt-16 pb-10 hero-gradient overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] animate-rotate-slow"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='100' cy='100' r='90' fill='none' stroke='%23D4AF37' stroke-width='0.5'/%3E%3Ccircle cx='100' cy='100' r='70' fill='none' stroke='%23D4AF37' stroke-width='0.5'/%3E%3Ccircle cx='100' cy='100' r='50' fill='none' stroke='%23D4AF37' stroke-width='0.5'/%3E%3C/svg%3E")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat'
          }} />
          
        </div>
        <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 pt-8 text-center">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-gold/80 mb-3">Sacred Vedic Traditions</span>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ivory mb-3">
            Discover Sacred Rituals
          </h1>
          <p className="text-ivory/70 text-base max-w-xl mx-auto font-body mb-8">
            Explore authentic Vedic rituals performed by verified priests — understand their meaning, significance, and how to book one.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl border border-gold/20">
              <Search size={18} className="text-maroon/40 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by ritual name, deity, or occasion…"
                className="flex-1 text-sm text-maroon placeholder-maroon/40 bg-transparent outline-none font-body" />
              
              {searchQuery &&
              <button onClick={() => setSearchQuery('')} className="text-maroon/40 hover:text-maroon transition-colors">
                  <X size={16} />
                </button>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-xl border-b border-gold/15 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            {/* Filter toggle (mobile) */}
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`flex-shrink-0 flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full border transition-all duration-200 ${
              activeFilterCount > 0 ?
              'bg-saffron text-white border-saffron' : 'border-gold/30 text-maroon hover:border-saffron hover:text-saffron'}`
              }>
              
              <SlidersHorizontal size={13} />
              Filters
              {activeFilterCount > 0 &&
              <span className="w-4 h-4 rounded-full bg-white text-saffron text-[9px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              }
            </button>

            {/* Category pills */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              {CATEGORIES.map((cat) =>
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`flex-shrink-0 text-xs font-semibold px-3 py-2 rounded-full border transition-all duration-200 ${
                selectedCategory === cat ?
                'bg-maroon text-ivory border-maroon' : 'border-gold/30 text-maroon/70 hover:border-maroon/40 hover:text-maroon'}`
                }>
                
                  {cat}
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="flex-shrink-0 ml-auto relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs font-semibold text-maroon bg-transparent border border-gold/30 rounded-full px-3 py-2 pr-7 outline-none cursor-pointer hover:border-saffron transition-colors appearance-none">
                
                {SORT_OPTIONS.map((opt) =>
                <option key={opt} value={opt}>{opt}</option>
                )}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-maroon/50 pointer-events-none" />
            </div>
          </div>

          {/* Expanded filters */}
          {filtersOpen &&
          <div className="mt-3 pt-3 border-t border-gold/15 flex flex-wrap gap-3 animate-fade-in-up">
              {/* Occasion filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-widest text-maroon/50">Occasion:</span>
                {OCCASIONS.map((occ) =>
              <button
                key={occ}
                onClick={() => setSelectedOccasion(occ)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all duration-200 ${
                selectedOccasion === occ ?
                'bg-saffron text-white border-saffron' : 'border-gold/30 text-maroon/70 hover:border-saffron/50'}`
                }>
                
                    {occ}
                  </button>
              )}
              </div>
              {/* Popular toggle */}
              <button
              onClick={() => setShowPopularOnly(!showPopularOnly)}
              className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-all duration-200 ${
              showPopularOnly ? 'bg-gold text-white border-gold' : 'border-gold/30 text-maroon/70 hover:border-gold/60'}`
              }>
              
                <Star size={11} className={showPopularOnly ? 'fill-white' : ''} />
                Popular Only
              </button>
              {activeFilterCount > 0 &&
            <button
              onClick={clearFilters}
              className="text-[11px] font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors">
              
                  <X size={11} />
                  Clear all
                </button>
            }
            </div>
          }
        </div>
      </div>

      {/* Results */}
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8 pb-28 md:pb-12">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-maroon/60 font-body">
            <span className="font-bold text-maroon">{filtered.length}</span> rituals found
          </p>
        </div>

        {filtered.length === 0 ?
        <div className="text-center py-20">
            <div className="text-5xl mb-4">🙏</div>
            <h3 className="font-display text-xl font-bold text-maroon mb-2">No rituals found</h3>
            <p className="text-maroon/50 text-sm font-body mb-4">Try adjusting your search or filters</p>
            <button onClick={clearFilters} className="text-saffron text-sm font-semibold hover:underline">
              Clear all filters
            </button>
          </div> :

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((ritual) =>
          <RitualCard key={ritual.id} ritual={ritual} />
          )}
          </div>
        }
      </main>

      <HomeBottomNav />
    </div>);

}